import { encodeSelectionContextV3, isAlineContentEditor, mentionType } from '@aline/common';
import type { Editor, Range } from 'slate';
import { Range as SlateRange } from 'slate';

import { serializeSelectionFromRange } from './serializeSelectionFromRange';
import type { SelectionV3MentionElement } from '../ChatPanel/ChatEditor/ChatEditorMention/utils';
import { MAX_SELECTION_MENTION_SIZE } from '../ChatPanel/ChatEditor/SelectionMentionPastePlugin';

export type BuildSelectionMentionV3Result =
  | { status: 'invalid' }
  | { status: 'too-large' }
  | { html: string; mention: SelectionV3MentionElement; status: 'ok' };

/**
 * Builds a v3 selection-mention element from an Aline editor + range, with no
 * side effects. v3 carries the pre-serialized HTML-like Cast context (including
 * the `<selection-start/>` / `<selection-end/>` span), produced by
 * `serializeSelectionFromRange` — the client-side glue over the editor-free
 * serializer in `@aline/common`.
 *
 * Returns a tagged result the caller can switch on:
 *  - `invalid`   — not an Aline content editor, no document info, or collapsed
 *    range.
 *  - `too-large` — the encoded context exceeds `MAX_SELECTION_MENTION_SIZE`.
 *  - `ok`        — `mention` is ready to insert into the chat input.
 */
export function buildSelectionMentionV3(
  alineEditor: Editor,
  range: Range,
  options: { focusSuggestionId?: string } = {}
): BuildSelectionMentionV3Result {
  if (!isAlineContentEditor(alineEditor) || !alineEditor.documentInfo) return { status: 'invalid' };
  if (SlateRange.isCollapsed(range)) return { status: 'invalid' };

  const { documentInfo } = alineEditor;
  const html = serializeSelectionFromRange(alineEditor, range);
  const focusSuggestionId = options.focusSuggestionId ?? null;

  const encoded = encodeSelectionContextV3({
    documentId: documentInfo.id,
    focusSuggestionId,
    html,
  });
  if (encoded.length > MAX_SELECTION_MENTION_SIZE) return { status: 'too-large' };

  const documentName = documentInfo.name || 'Untitled Document';
  const mention: SelectionV3MentionElement = {
    children: [{ text: '' }],
    documentId: documentInfo.id,
    documentName,
    focusSuggestionId,
    html,
    mentionTypeName: 'selection-v3',
    type: mentionType,
    value: documentName,
  };

  return { html, mention, status: 'ok' };
}
